import { bbAxios } from '@bb/session-manager';

import {
  FIND_SIMILAR_CONTEXT_ENDPOINT,
  FIND_SIMILAR_EDIT_ENDPOINT,
  FIND_SIMILAR_GENERAL_ENDPOINT,
  FIND_SIMILAR_PRESENCE_ENDPOINT,
  FIND_SIMILAR_SEMANTIC_ENDPOINT,
  METRICS_ENDPOINT,
  MODELS_ENDPOINT,
  MODELS_V2_ENDPOINT,
  ONLYOFFICE_CONFIG_ENDPOINT,
  TEXT_ENDPOINT,
  TRAINING_DELETE_BY_IDS_ENDPOINT,
  TRAINING_MODIFY_EDITS_ENDPOINT,
  TRAINING_RULE_DISABLE_ENDPOINT, // Added new endpoint import
  TRAINING_RULE_SECTIONS_ENDPOINT,
  TRAINING_RULES_ANSWER_QUESTIONS_ENDPOINT,
  TRAINING_RULES_ENDPOINT,
  TRAINING_RULES_IMPORT_RULES_ENDPOINT,
  TRAINING_RULES_REMOVE_SENTENCES_ENDPOINT,
  USER_INFO_TRACK_ENDPOINT,
} from '../endpoints.config';

import { MongoDataAPI } from './MongoDataAPI';
import { MongoStatsAPI } from './MongoStatsAPI';

export async function GetOnlyOfficeConfig(contractId) {
  const { data } = await bbAxios.get(
    `${ONLYOFFICE_CONFIG_ENDPOINT}?documentId=${contractId}`
  );
  return data;
}

export async function GetPresenceThresholdExamples(textData) {
  const { data } = await bbAxios.post(FIND_SIMILAR_PRESENCE_ENDPOINT, { ...textData });
  return data;
}

export async function GetContextThresholdExamples(textData) {
  const { data } = await bbAxios.post(FIND_SIMILAR_CONTEXT_ENDPOINT, { ...textData });
  return data;
}

export async function FindSimilar(data) {
  const { data: resp } = await bbAxios.post(FIND_SIMILAR_GENERAL_ENDPOINT, { ...data });
  return resp;
}

export async function EditSimilar(data) {
  const { data: resp } = await bbAxios.post(FIND_SIMILAR_EDIT_ENDPOINT, { ...data });
  return resp;
}

export async function FindSimilarSemantic(data) {
  const { data: resp } = await bbAxios.post(FIND_SIMILAR_SEMANTIC_ENDPOINT, { ...data });
  return resp;
}

export async function GetRule(id) {
  const { data } = await bbAxios.get(`${TRAINING_RULES_ENDPOINT}/${id}`);
  return data;
}

export async function UpdateRuleText(id, name, description, additionalConfig = {}) {
  const { data } = await bbAxios.put(`${TRAINING_RULES_ENDPOINT}/${id}`, {
    name,
    description,
    ...additionalConfig,
  });
  return data;
}

export async function ResetRuleUsage(id, name, description) {
  const { data } = await bbAxios.put(`${TRAINING_RULES_ENDPOINT}/${id}`, {
    name,
    description,
    usageCount: 0,
    usageContracts: [],
    usageUsers: [],
    lastUsed: null,
  });
  return data;
}

// Added new function to update rule enabled status
export async function UpdateRuleEnabledStatus(ruleId, isEnabled) {
  const payload = { disable: !isEnabled }; // Backend expects 'disable' boolean
  // The endpoint is POST /trainingrule/disable?ruleId=<ruleId>
  // The ruleId should be a query parameter, and the disable flag in the body
  const { data } = await bbAxios.post(
    `${TRAINING_RULE_DISABLE_ENDPOINT}?ruleId=${ruleId}`,
    payload
  );
  return data;
}

export async function UpdateRuleSection(id, section) {
  const { data } = await bbAxios.put(`${TRAINING_RULES_ENDPOINT}/${id}/${section}`);
  return data;
}

export async function MergeIntoRule(id, target, model) {
  const { data } = await bbAxios.put(`${TRAINING_RULES_ENDPOINT}/${id}/merge/${target}`, { model });
  return data;
}

export async function UpsertEditedSentence(
  data,
  model,
  ruleId,
  ruleStrengthDivisor,
  maxRuleStrength
) {
  const { data: respData } = await bbAxios.put(`${TRAINING_MODIFY_EDITS_ENDPOINT}`, {
    data,
    model,
    ruleId,
    ruleStrengthDivisor,
    maxRuleStrength,
    action: 'upsert',
  });
  if (!respData) {
    console.error('Error! Upsert training data failed.');
    return false;
  }
  return true;
}

export async function DeleteTrainingDataByIds(
  ids,
  model,
  ruleId,
  ruleStrengthDivisor,
  maxRuleStrength
) {
  const { data: respData } = await bbAxios.put(`${TRAINING_DELETE_BY_IDS_ENDPOINT}`, {
    ids,
    model,
    ruleId,
    ruleStrengthDivisor,
    maxRuleStrength,
    action: 'delete',
  });
  if (!respData) {
    console.error('Error! Delete training data by ids failed.');
    return false;
  }
  return true;
}

export async function GetRuleCommentsUnique(id) {
  const { data } = await bbAxios.get(`${TRAINING_RULES_ENDPOINT}/${id}?comments=true`);
  return data;
}

export async function FetchImportRulesData(modelId, contractType) {
  // Here we are going to use V2 API to fetch import rules data
  // NOTE: We could fetch all needed data in one request, but we'll do it in a less
  // efficient way to avoid making large changes to the existing UI code
  const trainingRuleAPI = new MongoDataAPI('TrainingRules');
  const trainingRuleSectionAPI = new MongoDataAPI('TrainingRuleSection');
  const trainingModelAPI = new MongoDataAPI('TrainingModel');
  const playbookQuery = (field) => ({
    model: { $ne: { [field]: modelId } },
    contractType,
  });
  const limit = 10000;
  const [rules, sections, playbooks] = await Promise.all([
    trainingRuleAPI.fetchData({
      fields: ['-trainingIds'], // Legacy field, exclude trainingIds to avoid large data transfer
      // populate: ['trainingCount'], // If we want real-time counting we can use this but takes longer
      query: playbookQuery('playbookIds'),
      limit,
      sort: ['name'],
    }),
    trainingRuleSectionAPI.fetchData({
      fields: ['-trainingIds'],
      query: { contractType },
      limit,
      sort: ['name'],
    }),
    trainingModelAPI.fetchData({
      fields: ['name', 'stage'],
      populate: ['trainingRuleCount'],
      query: { ...playbookQuery('_id'), stage: { $ne: 'backup' } },
      limit,
      sort: ['name'],
    }),
  ]);
  return {
    trainingRules: rules.docs,
    sections: sections.docs,
    models: playbooks.docs,
  };
}

export async function FetchTrainingCounts(ruleIds) {
  const trainingAPI = new MongoStatsAPI('Training');
  const { stats } = await trainingAPI.fetchStatsBulk(
    ruleIds.map((ruleId) => ({
      query: { training_rule_ids: ruleId?.toString() },
      stats: ['count'],
    }))
  );
  return stats;
}

export async function GetModelData(modelId, params = '', includeExamples = false) {
  try {
    const baseEndpoint = `${MODELS_ENDPOINT}/${modelId}`;

    // Build query parameters
    const queryParams = new URLSearchParams(params);
    if (includeExamples) {
      queryParams.set('includeExamples', 'true');
    }
    const queryString = queryParams.toString();
    const { data: respData } = await bbAxios.get(`${baseEndpoint}?${queryString}`);
    return {
      SUCCESS: true,
      ...respData,
    };
  } catch (err) {
    console.error('Error! Failed to retrieve model data with error:', err);
    return {
      SUCCESS: false,
      data: null,
    };
  }
}

export function UpdateModelAttributes(data) {
  const {
    _id: id,
    name,
    comment,
    isDeleteProtected,
    stage,
    defaultQuestionnaire,
    groups,
    enableBullseyeByEmail,
    generateAddendum,
    addendumTemplate,
    emailOnUpload,
    convertPdfToDocx,
    rasterizePdfConversion,
    bullseyeTrainingModels,
    nano_slot_model,
    enableRiskRouting,
    enableNewFsdMethod,
    enableEmbeddingAlignment,
    links,
    statisticalValidationConfig,
  } = data;
  return bbAxios.patch(`${MODELS_ENDPOINT}/${id}`, {
    name,
    comment,
    isDeleteProtected,
    stage,
    defaultQuestionnaire,
    groups,
    enableBullseyeByEmail,
    generateAddendum,
    addendumTemplate,
    emailOnUpload,
    convertPdfToDocx,
    rasterizePdfConversion,
    bullseyeTrainingModels,
    nano_slot_model,
    enableRiskRouting,
    enableNewFsdMethod,
    enableEmbeddingAlignment,
    links,
    statisticalValidationConfig,
  });
}

export async function GetTrainingModelSections(id) {
  try {
    const { data } = await bbAxios.get(`${TRAINING_RULE_SECTIONS_ENDPOINT}/${id}`);
    return data;
  } catch (err) {
    console.error(
      'Error! Failed to retrieve training rule sections data with error:',
      err
    );
    return [];
  }
}

export async function GetTrainingModelSectionsForContractType(contractType) {
  try {
    const { data } = await bbAxios.get(
      `${TRAINING_RULE_SECTIONS_ENDPOINT}/contractType/${contractType}`
    );
    if (!data) {
      console.error(
        `Error! No training rule sections data for contract type ${contractType} retrieved.`
      );
      return [];
    }
    return data;
  } catch (err) {
    console.error(err);
    return [];
  }
}

export async function GetTextBySection(sectionId, modelId, rule = null) {
  try {
    const queryString = `?sectionId=${sectionId}&modelId=${modelId}${rule ? `&rule=${rule}` : ''}`;
    const { data } = await bbAxios.get(`${TEXT_ENDPOINT}${queryString}`);
    if (!data) {
      console.error('Error! No text data retrieved.');
      return [];
    }
    return data;
  } catch (err) {
    console.error(err);
    return [];
  }
}

export async function GetTextBySearch(search, sectionId, modelId, rule = null) {
  try {
    const queryString = `?search=${search}&sectionId=${sectionId}&modelId=${modelId}${rule ? `&rule=${rule}` : ''}`;
    const { data } = await bbAxios.get(`${TEXT_ENDPOINT}${queryString}`);
    if (!data) {
      console.error('Error! No text data retrieved.');
      return [];
    }
    return data;
  } catch (err) {
    console.error(err);
    return [];
  }
}

export async function CreateTextData(data) {
  const { data: respData } = await bbAxios.post(`${TEXT_ENDPOINT}`, data);
  return respData;
}

export async function DeleteRule(modelId, ruleId) {
  try {
    const { data: respData } = await bbAxios.delete(`${TRAINING_RULES_ENDPOINT}`, {
      data: {
        model: modelId,
        rule: ruleId,
      },
    });
    return respData;
  } catch (err) {
    console.error('Error! Failed to delete training rule with error:', err);
  }
}

export async function WriteMetrics(data) {
  const { data: respData } = await bbAxios.post(METRICS_ENDPOINT, data);
  return respData;
}

export async function SaveImportRules(importParams) {
  const { data } = await bbAxios.post(
    `${TRAINING_RULES_IMPORT_RULES_ENDPOINT}?async=true`,
    importParams
  );
  return data;
}

export async function SaveAnswerQuestionsRules(data) {
  return bbAxios.post(`${TRAINING_RULES_ANSWER_QUESTIONS_ENDPOINT}?async=true`, data);
}

export async function GetUserInfoTrack(userId) {
  const { data } = await bbAxios.get(`${USER_INFO_TRACK_ENDPOINT}?userId=${userId}`);
  return data;
}

export async function GetUserHash() {
  try {
    const { data } = await bbAxios.get(USER_INFO_TRACK_ENDPOINT);
    return data;
  } catch (err) {
    console.error(
      'Error! Failed to retrieve user information tracking data with error:',
      err
    );
  }
}
